import * as React from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Link, Typography } from '@mui/material';
import BaseButton from '../../../components/BaseButton';
import BaseDateRangePicker, {
  INVALID_DATE_RANGE,
} from '../../../components/BaseDateRangePicker';
import BaseInput from '../../../components/BaseInput';
import DataStore from '../../../store';
import { validateForm } from '../../../helpers/formValidator';
import { notificationStore } from '../../../store/Notification';
import { MISSING_FIELDS_MESSAGE } from '../../../helpers/constants';
import { validationRules } from '../../../helpers/validationRules';
import { observer } from 'mobx-react';
import { DateTime } from 'luxon';
import TableAutoTriggering, {
  SlideRow,
} from '../../../components/TableAutoTriggering';

// Set to false to use the real backend request.
const USE_MOCK_DATA = false;

// AWS Batch only keeps job info for about a week, so older submissions
// have no segmentation / classification status.
const JOB_INFO_RETENTION_DAYS = 7;
const SUBMITTED_AT_FORMAT = 'yyyy-LL-dd HH:mm:ss';

const MOCK_SLIDES: SlideRow[] = [
  {
    slideId: '0004A',
    refDb: 'Human Gut Microbiome Experimental v10.2',
    sampleType: 'Stool',
    labellingMode: 'GTAC',
    submissionStatus: 'Submitted',
    logStream: '2026/10/19/[$LATEST]a72012c4e9b34f1d8a6c0e5b7d3f9a21',
    skipReason: null,
    submittedAt: '2026-10-19 15:53:44',
    segmentationStatus: 'SUCCEEDED',
    classificationStatus: 'RUNNING',
    segmentationJobId: '3f6c1a52-8d1e-4c0b-9a57-0e1b7c2d9a11',
    classificationJobId: 'b7d24e90-5a3c-4f68-8e12-6c9a0d4f7b22',
  },
  {
    slideId: '0004A',
    refDb: 'Human Gut Microbiome Experimental v10.2',
    sampleType: 'Fermentation',
    labellingMode: 'GTAC',
    submissionStatus: 'Failed',
    logStream: '2026/10/19/[$LATEST]5be83d07c1a94e2f9b4d6a8c0e1f3b72',
    skipReason: 'Sample type Fermentation is not supported',
    submittedAt: '2026-10-19 11:20:05',
    segmentationStatus: null,
    classificationStatus: null,
    segmentationJobId: null,
    classificationJobId: null,
  },
  {
    slideId: '0004A',
    refDb: 'Human Gut Microbiome Experimental v10.2',
    sampleType: 'Stool',
    labellingMode: 'GTAC',
    submissionStatus: 'Submitted',
    logStream: '2026/10/18/[$LATEST]c04f9a1e7d2b48c5a3e6b8d0f1a7c935',
    skipReason: null,
    submittedAt: '2026-10-18 09:41:32',
    segmentationStatus: 'RUNNING',
    classificationStatus: 'PENDING',
    segmentationJobId: 'c91e5b07-2f4a-4d83-b6a0-1d8e3f5c7a33',
    classificationJobId: 'e24a8c16-7b9d-4e51-a3f2-9b0c6d1e8f44',
  },
  {
    slideId: '0004A',
    refDb: 'Human Gut Microbiome Experimental v10.2',
    sampleType: 'DNA',
    labellingMode: 'GTAC',
    submissionStatus: 'Failed',
    logStream: '2026/10/17/[$LATEST]e18d3b6a5c0f4972b1d8a4e7c9f2b063',
    skipReason: 'Batch submit failed.',
    submittedAt: '2026-10-17 16:08:57',
    segmentationStatus: null,
    classificationStatus: null,
    segmentationJobId: null,
    classificationJobId: null,
  },
  {
    slideId: '0004A',
    refDb: 'Human Gut Microbiome Experimental v10.2',
    sampleType: 'Tissue',
    labellingMode: 'GTAC',
    submissionStatus: 'Submitted',
    logStream: '2026/10/05/[$LATEST]9d2f6a8b1c3e4057a8b9c0d1e2f3a456',
    skipReason: null,
    submittedAt: '2026-10-05 10:12:09',
    segmentationStatus: null,
    classificationStatus: null,
    segmentationJobId: 'a1b2c3d4-5e6f-4a7b-8c9d-0e1f2a3b4c55',
    classificationJobId: 'f6e5d4c3-b2a1-4f0e-9d8c-7b6a5f4e3d66',
  },
];

const AutoTriggering = observer(() => {
  const [errors, setErrors] = React.useState({});
  const [slides, setSlides] = React.useState<SlideRow[]>([]);
  const DATE_RANGE_LAST_DAY = 'last day';
  const DATE_RANGE_LAST_WEEK = 'last week';
  const DATE_RANGE_LAST_MONTH = 'last month';
  const DATE_RANGE_NO_LIMIT = 'clear';
  const [data, setData] = React.useState({
    slide_id: '',
    dateRange: null as string | null,
  });

  const fieldLabels: { [key: string]: string } = {
    slide_id: 'Slide Number/Slide ID',
    dateRange: 'Date Range',
  };

  const validationSchema = {
    slide_id: {
      type: 'string',
      required: false,
      // Slide number (1234) or slide ID (1234A)
      custom: (value: string) =>
        !value ||
        validationRules.slideNumber.pattern.test(value.trim()) ||
        validationRules.slideId.pattern.test(value.trim())
          ? undefined
          : 'Slide Number/ID must be 3-5 digits, optionally followed by a letter A-H.',
    },
  };

  const onChangeData = (event: any) => {
    const { name, value } = event.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const showSlides = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();

    if (data.dateRange === INVALID_DATE_RANGE) {
      notificationStore.showMessage('Some fields are invalid.', 'error', {
        'Date Range': 'Date Range is not in a valid format.',
      });
      return;
    }

    const payload = {
      ...data,
      dateRange: data.dateRange || null,
    };

    const validError = validateForm(payload, validationSchema);
    if (validError) {
      const humanizedErrors = Object.entries(validError).reduce(
        (acc: any, [key, message]) => {
          acc[fieldLabels[key] || key] = message;
          return acc;
        },
        {}
      );
      notificationStore.showMessage(
        MISSING_FIELDS_MESSAGE,
        'error',
        humanizedErrors
      );
      setErrors(humanizedErrors);
      return;
    }

    setErrors({});
    if (USE_MOCK_DATA) {
      let [from, to] = (payload.dateRange || '').split('-');
      if (!payload.slide_id && !payload.dateRange) {
        from = DateTime.now()
          .minus({ days: JOB_INFO_RETENTION_DAYS })
          .toFormat('yyyyLLdd');
      }
      const filtered = MOCK_SLIDES.filter((slide) => {
        const day = slide.submittedAt.slice(0, 10).replace(/-/g, '');
        return (
          (!payload.slide_id ||
            slide.slideId
              .toLowerCase()
              .includes(payload.slide_id.toLowerCase())) &&
          (!from || day >= from) &&
          (!to || day <= to)
        );
      });
      setSlides(filtered);
      return;
    }
    const resData = await DataStore.sampleManagerAutoTriggeringRequest(payload);
    const responseSlides = resData?.response?.slides || [];
    setSlides(responseSlides);
  };

  const getDateRangeValues = (type: string, number: number) => {
    const now = DateTime.now().startOf('day');
    const from = now.minus({ [type]: number }).toFormat('yyyyLLdd');
    const to = now.toFormat('yyyyLLdd');

    return `${from}-${to}`;
  };

  const getPresetDateRange = (option: string) => {
    switch (option) {
      case DATE_RANGE_LAST_DAY:
        return getDateRangeValues('days', 1);
      case DATE_RANGE_LAST_WEEK:
        return getDateRangeValues('weeks', 1);
      case DATE_RANGE_LAST_MONTH:
        return getDateRangeValues('months', 1);
      default:
        return '';
    }
  };

  const getSelectDateOptions = () => {
    const options = [
      { label: 'day', value: DATE_RANGE_LAST_DAY },
      { label: 'week', value: DATE_RANGE_LAST_WEEK },
      { label: 'month', value: DATE_RANGE_LAST_MONTH },
      { label: 'clear', value: DATE_RANGE_NO_LIMIT },
    ];

    return (
      <Box display='flex' alignItems='center' justifyContent='flex-end'>
        <Typography variant='body2' sx={{ mr: 0.5 }}>
          last:
        </Typography>
        {options.map((option, index) => (
          <Box key={option.label} display='flex' alignItems='center'>
            <Link
              component='button'
              variant='body2'
              onClick={() =>
                setData((prev) => ({
                  ...prev,
                  dateRange:
                    option.value === DATE_RANGE_NO_LIMIT
                      ? ''
                      : getPresetDateRange(option.value),
                }))
              }
              sx={{
                fontWeight: 600,
                textTransform: 'lowercase',
                mx: index < options.length - 1 ? 0.5 : 0,
              }}
            >
              {option.label}
            </Link>
            {index < options.length - 1 && (
              <Typography variant='body2' sx={{ mx: 0.5 }}>
                |
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const jobInfoCutoff = DateTime.now().minus({
    days: JOB_INFO_RETENTION_DAYS,
  });
  const hasOlderSubmissions = slides.some(
    (slide) =>
      DateTime.fromFormat(slide.submittedAt, SUBMITTED_AT_FORMAT) <
      jobInfoCutoff
  );

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <BaseInput
            label={fieldLabels.slide_id}
            value={data.slide_id}
            name='slide_id'
            onChange={onChangeData}
            errors={errors}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <BaseDateRangePicker
            value={data.dateRange ?? ''}
            name='dateRange'
            onChange={onChangeData}
            errors={errors}
            description={getSelectDateOptions()}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box display='flex' alignItems='center' gap={2}>
            <BaseButton
              text='Show'
              variant='contained'
              color='primary'
              isDisabled={DataStore.isProcessing}
              spinner={DataStore.isProcessing}
              onClick={showSlides}
            />
            <Typography variant='body2' color='text.secondary'>
              If no filters are used, last 7 days will be shown.
            </Typography>
          </Box>
        </Grid>
      </Grid>

      {slides.length > 0 ? (
        <>
          <TableAutoTriggering slides={slides} />
          {hasOlderSubmissions && (
            <Typography variant='body2' color='text.secondary' sx={{ mt: 2 }}>
              AWS keeps job statuses for {JOB_INFO_RETENTION_DAYS} days, older
              job statuses will not be shown.
            </Typography>
          )}
        </>
      ) : (
        <Typography variant='body2' sx={{ mt: 3 }}>
          No job submissions to display.
        </Typography>
      )}
    </>
  );
});

export default AutoTriggering;
